import { HitProps } from '@components/algolia/Hit';
import { PRODUCTS, PRODUITS } from '@utils/constants';
import { Swatch } from '@hooks/useHitSwatches';
import { setSelectItemCookie } from '@utils/gtm';
import { trackProductEvent } from '@utils/gtm/ga4';
import { GA4_EVENT_NAMES } from '@utils/gtm/ga4/constants';
import { getGenderFromString } from '@utils/gtm/ga4/utils';

const { VIEW_ITEM_LIST, SELECT_ITEM } = GA4_EVENT_NAMES;

type Level = keyof HitProps['categories'];

const excludeNonProductCategories = (cat: HitProps['categories'][Level]) =>
  cat?.filter(
    (category) =>
      category.toLowerCase().startsWith(PRODUCTS) ||
      category.toLowerCase().startsWith(PRODUITS)
  ) ?? [];

const swapBracketForSlash = (category: string) => category.replace(/>/g, '/'); // "Products > Packs and bags > Daypacks and school bags > Daypacks" => "products/packs and bags/daypacks and school bags/daypacks"

const grabCategory = (categories: HitProps['categories']) => {
  const levels: Level[] = [
    'lvl6',
    'lvl5',
    'lvl4',
    'lvl3',
    'lvl2',
    'lvl1',
    'lvl0',
  ];

  for (const level of levels) {
    const filteredCategory = excludeNonProductCategories(categories[level]);
    if (filteredCategory && filteredCategory.length > 0) {
      return swapBracketForSlash(filteredCategory[0]);
    }
  }

  return '';
};

const formatCategory = (category: string) =>
  category
    .split('/')
    .flatMap((cat) => {
      const lowerCasedCategory = cat.toLowerCase().trim();
      if (lowerCasedCategory === PRODUCTS || lowerCasedCategory === PRODUITS) {
        return [];
      }

      return lowerCasedCategory;
    })
    .join('/');

export const getList = () =>
  window.location.href.includes('/search')
    ? 'Search Results'
    : 'Collection Results';

export const createDataLayerProduct = (
  product: HitProps,
  activeVariant?: Swatch
) => ({
  code: product.objectID,
  gaName: product.title,
  selectedPrice: {
    price: {
      value: activeVariant?.salePrice ?? 0,
    },
    basePrice: {
      value: activeVariant?.price ?? 0,
    },
  },
  index: product.__position,
  brand: {
    name: product.brand,
  },
  category: formatCategory(grabCategory(product.categories)),
  itemListName: getList(),
  selectedColour: activeVariant?.swatchName ?? null,
  gender: getGenderFromString(product.title),
  promoBadge: { code: activeVariant?.badge?.code ?? null },
  variantId: activeVariant?.sku ?? null,
  hasMemberPriceDifference: activeVariant?.hasMemberPriceDifference ?? false,
});

// this is currently not used due to: https://mountainequipment.atlassian.net/wiki/spaces/ER/pages/405667845/2024+02+08
export const pushProductImpressionsToDataLayer = (products: HitProps[]) => {
  const dataLayerProducts = products.map((product) =>
    createDataLayerProduct(product)
  );

  trackProductEvent({
    eventName: VIEW_ITEM_LIST,
    products: dataLayerProducts,
    itemListName: getList(),
  });
};

export const pushProductClickToDataLayer = (
  product: HitProps,
  activeVariant: Swatch
) => {
  const dataLayerProduct = createDataLayerProduct(product, activeVariant);
  setSelectItemCookie({
    eventName: SELECT_ITEM,
    products: [dataLayerProduct],
    itemListName: getList(),
  });
};
